import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import vfs from '../../lib/virtualFS';
import {
  Send, Bot, User, Loader2, Cpu, Settings, ChevronDown,
  FileCode, Play, FolderTree, Sparkles
} from 'lucide-react';

const MAX_CONTEXT_LINES_PER_FILE = 200;

function ToolBadge({ tool, args }) {
  const icons = { run_python: Play, read_file: FileCode, write_file: FileCode, list_files: FolderTree, add_cell: Play };
  const Icon = icons[tool] || Cpu;
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] text-neutral-400 font-mono">
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {tool}({typeof args === 'string' ? args : JSON.stringify(args).slice(0, 40)})
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : ''}`} data-testid={`chat-msg-${msg.role}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bot className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
        </div>
      )}
      <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm leading-relaxed ${
        isUser
          ? 'bg-white/10 text-neutral-200'
          : 'bg-white/[0.03] border border-white/[0.06] text-neutral-300'
      }`}>
        {msg.tool && <ToolBadge tool={msg.tool} args={msg.toolArgs} />}
        <ReactMarkdown
          components={{
            code: ({ inline, children, ...props }) =>
              inline
                ? <code className="bg-white/10 px-1 py-0.5 rounded text-xs font-mono text-green-300" {...props}>{children}</code>
                : <pre className="bg-black/40 rounded-lg p-3 my-2 overflow-x-auto text-xs font-mono text-green-300"><code {...props}>{children}</code></pre>,
            p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
          }}
        >
          {msg.content}
        </ReactMarkdown>
      </div>
      {isUser && (
        <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <User className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
        </div>
      )}
    </div>
  );
}

function ModelButton({ model, transformers }) {
  const isActive = transformers.selectedModel === model.id && transformers.modelLoaded;
  const providerLabel = model.provider === 'huggingface'
    ? 'Hosted'
    : model.provider === 'custom'
      ? 'Custom'
      : 'Local';

  return (
    <div
      className={`w-full flex items-start justify-between gap-4 px-3 py-2 rounded-lg text-left text-xs transition-all ${
        isActive
          ? 'bg-white/10 border border-white/20 text-white'
          : 'bg-white/[0.02] border border-white/[0.06] text-neutral-400 hover:border-white/15'
      }`}
      data-testid={`model-option-${model.name}`}
    >
      <button
        onClick={() => transformers.loadModel(model.id)}
        disabled={transformers.modelLoading}
        className="flex-1 text-left disabled:opacity-60"
      >
        <div className="font-medium">{model.name}</div>
        <div className="text-[10px] text-neutral-600 mt-0.5">{model.description || model.size}</div>
      </button>
      <div className="text-right text-[10px] text-neutral-500 flex flex-col items-end gap-1">
        <div>{providerLabel}</div>
        {isActive && <div className="text-green-400">Active</div>}
        {model.configurable && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              transformers.editModel(model.id);
            }}
            disabled={transformers.modelLoading}
            className="px-1.5 py-0.5 rounded border border-white/10 text-neutral-400 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50"
            data-testid={`model-edit-${model.name}`}
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

function formatFileWithLineNumbers(name, content, maxLines = MAX_CONTEXT_LINES_PER_FILE) {
  if (content === null || content === undefined) {
    return `File: ${name}\n<missing>`;
  }

  const lines = String(content).split('\n');
  const visibleLines = lines.slice(0, maxLines);
  const lineNumberWidth = String(Math.max(visibleLines.length, 1)).length;
  const numbered = visibleLines
    .map((line, index) => `${String(index + 1).padStart(lineNumberWidth, ' ')}| ${line}`)
    .join('\n');
  const truncated = lines.length > maxLines
    ? `\n... (${lines.length - maxLines} more lines truncated)`
    : '';

  return `File: ${name}\n${numbered}${truncated}`;
}

function getMentionedFiles(text, files, excludedFiles = []) {
  const lowerText = text.toLowerCase();
  const excluded = new Set(excludedFiles.filter(Boolean));

  return files.filter((file) => {
    if (excluded.has(file)) {
      return false;
    }
    return lowerText.includes(file.toLowerCase());
  });
}

export default function ChatSidebar({ runPython, pyodideReady, transformers, notebookActions, activeFile }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm **Codelves** - your browser-based AI coding assistant.\n\nI can help you:\n- Write and run Python code in notebook cells\n- Create and edit your files\n- Plan and build projects\n\nTry: *\"create hello.py with a hello world\"* or *\"add a cell that prints fibonacci\"*",
    },
  ]);
  const [input, setInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showModels, setShowModels] = useState(false);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);
  const pendingWrite = useRef(null);
  const resizeInput = useCallback(() => {
    const textarea = inputRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = '0px';
    const maxHeight = 240;
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${Math.max(nextHeight, 40)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const localModels = transformers.availableModels.filter((model) => model.provider === 'local');
  const hostedModels = transformers.availableModels.filter((model) => model.provider !== 'local');
  const activeProvider = transformers.activeModel?.provider === 'huggingface'
    ? 'Hosted'
    : transformers.activeModel?.provider === 'custom'
      ? 'Custom'
      : transformers.modelLoaded
        ? 'Local'
        : 'Idle';
  const localBackendLabel = transformers.activeModel?.provider === 'local' && transformers.localBackend
    ? transformers.localBackend.toUpperCase()
    : null;

  const scrollToBottom = () => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollToBottom, [messages]);
  useEffect(() => {
    resizeInput();
  }, [input, resizeInput]);

  const tools = useCallback(() => ({
    list_files: () => {
      const files = vfs.getFileList();
      return `Files in workspace:\n${files.map(f => `  - ${f}`).join('\n')}`;
    },
    read_file: (name) => {
      const content = vfs.readFile(name);
      return content !== null ? `\`\`\`python\n${content}\n\`\`\`` : `File "${name}" not found.`;
    },
    write_file: (name, content) => {
      vfs.writeFile(name, content);
      if (notebookActions?.current?.switchToFile) {
        notebookActions.current.switchToFile(name);
      }
      return `Wrote ${content.split('\n').length} lines to \`${name}\` and opened it in the editor.`;
    },
    add_cell: (code) => {
      if (notebookActions?.current?.addCellWithCode) {
        const cellId = notebookActions.current.addCellWithCode(code);
        return `Added notebook cell [${cellId}] with your code. Click **Run** to execute it.`;
      }
      return 'Could not add cell - notebook not available.';
    },
    run_python: async (code) => {
      if (!pyodideReady) {
        return 'Pyodide is still loading...';
      }
      if (notebookActions?.current?.addCellWithCode) {
        notebookActions.current.addCellWithCode(code);
      }
      const { output, error } = await runPython(code);
      return error ? `Error:\n\`\`\`\n${error}\n\`\`\`` : `Output:\n\`\`\`\n${output}\n\`\`\``;
    },
  }), [runPython, pyodideReady, notebookActions]);

  const parseToolCall = useCallback((text) => {
    const patterns = [
      { regex: /(?:run|execute)\s+(?:the\s+)?(?:code\s+in\s+)?(?:file\s+)?[`"']?(\w+\.py)[`"']?/i, tool: 'run_file' },
      { regex: /(?:list|show)\s+(?:all\s+)?files/i, tool: 'list_files' },
      { regex: /(?:read|open|show|cat)\s+[`"']?(\w+\.\w+)[`"']?/i, tool: 'read_file' },
      { regex: /(?:create|write|make|save)\s+(?:a\s+)?(?:file\s+)?(?:called\s+)?[`"']?(\w+\.\w+)[`"']?\s+(?:with|containing|that\s+has)\s+([\s\S]+)/i, tool: 'write_file_with_content' },
      { regex: /(?:create|write|make|save)\s+(?:a\s+)?(?:file\s+)?(?:called\s+)?[`"']?(\w+\.\w+)[`"']?/i, tool: 'write_file' },
      { regex: /(?:add|insert|new)\s+(?:a\s+)?(?:notebook\s+)?cell\s+(?:with|that|containing)\s+([\s\S]+)/i, tool: 'add_cell' },
      { regex: /```python\n([\s\S]*?)```/i, tool: 'run_code' },
    ];
    for (const pattern of patterns) {
      const match = text.match(pattern.regex);
      if (match) {
        return { tool: pattern.tool, match };
      }
    }
    return null;
  }, []);

  const generateCodeFromDescription = (description) => {
    const desc = description.toLowerCase().trim();
    if (desc.includes('hello world') || desc.includes('hello')) {
      return 'print("Hello, World!")';
    }
    if (desc.includes('fibonacci') || desc.includes('fib')) {
      return 'def fibonacci(n):\n    a, b = 0, 1\n    for _ in range(n):\n        print(a, end=" ")\n        a, b = b, a + b\n    print()\n\nfibonacci(10)';
    }
    if (desc.includes('factorial')) {
      return 'def factorial(n):\n    return 1 if n <= 1 else n * factorial(n - 1)\n\nfor i in range(10):\n    print(f"{i}! = {factorial(i)}")';
    }
    if (desc.includes('sort') || desc.includes('bubble')) {
      return 'def bubble_sort(arr):\n    n = len(arr)\n    for i in range(n):\n        for j in range(0, n-i-1):\n            if arr[j] > arr[j+1]:\n                arr[j], arr[j+1] = arr[j+1], arr[j]\n    return arr\n\ndata = [64, 34, 25, 12, 22, 11, 90]\nprint(f"Sorted: {bubble_sort(data)}")';
    }
    if (desc.includes('class') || desc.includes('object')) {
      return 'class Animal:\n    def __init__(self, name, sound):\n        self.name = name\n        self.sound = sound\n\n    def speak(self):\n        return f"{self.name} says {self.sound}!"\n\ncat = Animal("Cat", "Meow")\ndog = Animal("Dog", "Woof")\nprint(cat.speak())\nprint(dog.speak())';
    }
    if (desc.includes('api') || desc.includes('request') || desc.includes('fetch')) {
      return '# Note: requests is not available in Pyodide\n# Use urllib or js.fetch instead\nimport json\n\ndata = {"message": "Hello from Codelves!"}\nprint(json.dumps(data, indent=2))';
    }
    if (desc.includes('plot') || desc.includes('chart') || desc.includes('graph')) {
      return '# Matplotlib example\nimport sys\nprint("Plotting libraries need to be loaded first")\nprint(f"Python version: {sys.version}")';
    }
    return `# ${description}\nprint("TODO: implement ${description}")`;
  };

  const buildModelContext = useCallback((userText) => {
    const files = vfs.getFileList();
    const sections = [];

    if (activeFile) {
      const activeContent = vfs.readFile(activeFile);
      if (activeContent !== null) {
        sections.push(`Active file:\n${formatFileWithLineNumbers(activeFile, activeContent)}`);
      }
    }

    const mentionedFiles = getMentionedFiles(userText, files, activeFile ? [activeFile] : []);
    mentionedFiles.forEach((file) => {
      const content = vfs.readFile(file);
      if (content !== null) {
        sections.push(`Mentioned file:\n${formatFileWithLineNumbers(file, content)}`);
      }
    });

    const contextSections = sections.length
      ? `\n\nFile context:\n${sections.join('\n\n')}`
      : '';

    return `You are Codelves, an AI coding assistant running in the browser. Workspace files: ${files.join(', ')}.${contextSections}\n\nUser: ${userText}\nAssistant:`;
  }, [activeFile]);

  const handleToolExecution = useCallback(async (userMsg) => {
    const t = tools();
    const parsed = parseToolCall(userMsg);

    if (pendingWrite.current) {
      const filename = pendingWrite.current;
      pendingWrite.current = null;
      const content = userMsg;
      const result = t.write_file(filename, content);
      return { tool: 'write_file', toolArgs: filename, content: result };
    }

    if (parsed) {
      switch (parsed.tool) {
        case 'list_files': {
          const result = t.list_files();
          return { tool: 'list_files', toolArgs: '', content: result };
        }
        case 'read_file': {
          const result = t.read_file(parsed.match[1]);
          return { tool: 'read_file', toolArgs: parsed.match[1], content: result };
        }
        case 'write_file_with_content': {
          const filename = parsed.match[1];
          const description = parsed.match[2];
          const code = generateCodeFromDescription(description);
          const result = t.write_file(filename, `# ${filename}\n${code}\n`);
          return { tool: 'write_file', toolArgs: filename, content: result };
        }
        case 'write_file': {
          pendingWrite.current = parsed.match[1];
          return { tool: 'write_file', toolArgs: parsed.match[1], content: `I'll create \`${parsed.match[1]}\`. Send me the code to put in it - or describe what it should do.` };
        }
        case 'add_cell': {
          const description = parsed.match[1];
          const code = generateCodeFromDescription(description);
          const result = t.add_cell(code);
          return { tool: 'add_cell', toolArgs: 'new cell', content: result };
        }
        case 'run_file': {
          const code = vfs.readFile(parsed.match[1]);
          if (!code) {
            return { content: `File \`${parsed.match[1]}\` not found.` };
          }
          const result = await t.run_python(code);
          return { tool: 'run_python', toolArgs: parsed.match[1], content: result };
        }
        case 'run_code': {
          const result = await t.run_python(parsed.match[1]);
          return { tool: 'run_python', toolArgs: 'inline', content: result };
        }
        default:
          break;
      }
    }

    return null;
  }, [tools, parseToolCall]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || generating) {
      return;
    }

    setInput('');
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setGenerating(true);

    const toolResult = await handleToolExecution(text);

    if (toolResult) {
      setMessages(prev => [...prev, { role: 'assistant', ...toolResult }]);
      setGenerating(false);
      return;
    }

    if (transformers.modelLoaded) {
      const context = buildModelContext(text);
      const result = await transformers.generate(context, { maxTokens: 200 });
      setMessages(prev => [...prev, { role: 'assistant', content: result || 'I generated a response but it was empty. Try a different prompt or switch models.' }]);
    } else {
      const fsContext = vfs.getFileList().join(', ');
      let response = `I can help with that! Files: \`${fsContext}\`.\n\n`;
      response += 'Try:\n';
      response += '- "create hello.py with a hello world"\n';
      response += '- "add a cell that prints fibonacci"\n';
      response += '- "run main.py" to execute a file\n';
      response += '- "read main.py" to view a file\n';
      response += '- Send code in a ```python block to run it\n\n';
      response += '_Load a local or hosted model below for free-form conversation._';
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
    }

    setGenerating(false);
  };

  return (
    <div className="h-full flex flex-col bg-[#080808]" data-testid="chat-sidebar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
          <span className="font-heading text-sm font-medium text-white">Codelves</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-neutral-500 font-mono">agent</span>
        </div>
        <button
          onClick={() => setShowModels(!showModels)}
          className="flex items-center gap-1 text-xs text-neutral-500 hover:text-white transition-colors"
          data-testid="model-settings-button"
        >
          <Settings className="w-3.5 h-3.5" strokeWidth={1.5} />
          <ChevronDown className={`w-3 h-3 transition-transform ${showModels ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {showModels && (
        <div className="px-3 py-3 border-b border-white/[0.06] bg-white/[0.01] space-y-3" data-testid="model-panel">
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">Local Browser Models</p>
            {localModels.map((model) => (
              <ModelButton key={model.id} model={model} transformers={transformers} />
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">Hosted Fallback</p>
            {hostedModels.map((model) => (
              <ModelButton key={model.id} model={model} transformers={transformers} />
            ))}
          </div>
          {transformers.customModelConfigured && (
            <p className="text-[10px] text-neutral-500 px-2">
              Custom endpoint saved in this browser. Use Edit to change it.
            </p>
          )}
          {transformers.localCustomModelConfigured && (
            <p className="text-[10px] text-neutral-500 px-2">
              Custom local model saved in this browser. Use Edit to change it.
            </p>
          )}
          {transformers.huggingFaceTokenConfigured && (
            <p className="text-[10px] text-neutral-500 px-2">
              Hugging Face token saved in this browser. Use Edit to change it.
            </p>
          )}
          {transformers.modelLoading && (
            <div className="flex items-center gap-2 px-2 py-1">
              <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />
              <div className="flex-1">
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-white/20 rounded-full transition-all" style={{ width: `${transformers.loadProgress}%` }} />
                </div>
              </div>
              <span className="text-[10px] text-neutral-500">{transformers.loadProgress}%</span>
            </div>
          )}
          {transformers.error && (
            <div className="flex items-start gap-2 px-2 py-2 rounded border border-red-500/30 bg-red-500/10" data-testid="model-error">
              <p className="flex-1 max-h-32 overflow-y-auto text-[11px] text-red-300 leading-snug break-words pr-1">{transformers.error}</p>
              <button
                onClick={() => transformers.clearError?.()}
                className="flex-shrink-0 text-red-300/70 hover:text-white text-sm leading-none px-1"
                aria-label="Dismiss error"
                data-testid="model-error-dismiss"
                type="button"
              >
                &times;
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-white/[0.04] text-[10px]">
        <span className="flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-full ${pyodideReady ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
          <span className="text-neutral-500">Python</span>
        </span>
        <span className="flex items-center gap-1">
          <span className={`w-1.5 h-1.5 rounded-full ${
            transformers.error ? 'bg-red-400' :
            transformers.modelLoading ? 'bg-yellow-400 animate-pulse' :
            transformers.modelLoaded ? 'bg-green-400' : 'bg-neutral-600'
          }`} />
          <span className="text-neutral-500">
            Model: {transformers.modelLoading
              ? `Loading ${transformers.loadProgress || 0}%`
              : `${activeProvider}${localBackendLabel ? ` (${localBackendLabel})` : ''}`}
          </span>
        </span>
        {transformers.error && !showModels && (
          <button
            onClick={() => setShowModels(true)}
            className="ml-auto text-[10px] text-red-400 hover:text-red-300 underline underline-offset-2"
            data-testid="model-error-show-panel"
            type="button"
          >
            View model error
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4" data-testid="chat-messages">
        {messages.map((msg, i) => (
          <ChatMessage key={i} msg={msg} />
        ))}
        {generating && (
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Thinking...
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      <div className="px-3 pb-3 pt-1">
        <div className="flex items-end gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 focus-within:border-white/25 transition-colors">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
            className="flex-1 min-h-[40px] bg-transparent text-sm text-white placeholder-neutral-600 resize-none focus:outline-none"
            placeholder="Ask Codelves anything..."
            data-testid="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || generating}
            className="p-1.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="chat-send-button"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
